import React from "react";
import { getDisplayStatus, getStatusTextClass } from "../utils/statusText";

type ResourceContextPanelProps = {
  resourceName?: string;
  resourceNameLabel?: string;
  status?: string;
  statusDetail?: string;
  currentSize?: string;
  currentSizeLabel?: string;
  currentSizeValueLabel?: string;
  currentSizeDetail?: string;
  currentSizeDetailLabel?: string;
  currentSizeDetails?: Array<{ label: string; value: string }>;
  portalUrl?: string | null;
  portalLinks?: Array<{ label: string; url: string }>;
  resourceLink?: { heading: string; label: string; url: string };
};

const ResourceContextPanel = ({ resourceName, resourceNameLabel = "Resource Name", status, statusDetail, currentSize, currentSizeLabel = "Current Size", currentSizeValueLabel = "Pool", currentSizeDetail, currentSizeDetailLabel = "Per DB", currentSizeDetails, portalUrl, portalLinks, resourceLink }: ResourceContextPanelProps) => {
  // Same labels on every page: Azure Running/Stopped show as Online/Offline
  const displayStatus = getDisplayStatus(status);
  const links = portalLinks ?? (portalUrl ? [{ label: "Open in Azure Portal", url: portalUrl }] : []);
  const sizeDetails = currentSizeDetails ?? (currentSizeDetail !== undefined
    ? [{ label: currentSizeDetailLabel, value: currentSizeDetail }]
    : []);
  const fieldCount = 1 + (status !== undefined ? 1 : 0) + (currentSize !== undefined ? 1 : 0) + (resourceLink ? 1 : 0);
  const gridColumnsClass = fieldCount >= 3
    ? "sm:grid-cols-[auto_auto_minmax(0,1fr)]"
    : "sm:grid-cols-[auto_minmax(0,1fr)]";

  return (
    <section className="workspace-card overflow-hidden border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <h3 className="text-xs font-semibold text-slate-800">Resource Information</h3>
      </div>
      <div className={`grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-3 ${gridColumnsClass}`}>
        <div className="min-w-0">
          <p className="text-[10px] font-medium leading-4 text-slate-500">{resourceNameLabel}</p>
          {links.length > 0 ? (
            <div className="mt-0.5 space-y-0.5">
              {links.map((link) => (
                <a key={link.url} href={link.url} target="_blank" rel="noreferrer" title={link.url} className="block truncate text-[11px] font-medium leading-4 text-blue-600 hover:text-blue-700">
                  {portalLinks ? link.label : resourceName || link.label}
                </a>
              ))}
            </div>
          ) : (
            <p className="mt-0.5 block min-h-4 truncate text-[11px] leading-4 text-slate-800" title={resourceName || "Not available"}>{resourceName || "Not available"}</p>
          )}
        </div>
        {status !== undefined && (
          <div className="min-w-0 sm:border-l sm:border-slate-200 sm:pl-6">
            <p className="text-[10px] font-medium leading-4 text-slate-500">Status</p>
            <div className="mt-0.5 flex min-h-4 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] leading-4">
              {/* Status as the API returns it, only the colour is mapped */}
              <span className={`font-medium ${getStatusTextClass(displayStatus)}`} title={displayStatus || "Not available"}>
                {displayStatus || "Not available"}
              </span>
              {statusDetail && (
                <span className="min-w-0 text-slate-600" title={statusDetail}>{statusDetail}</span>
              )}
            </div>
          </div>
        )}
        {currentSize !== undefined && (
          <div className="min-w-0 sm:border-l sm:border-slate-200 sm:pl-6">
            <p className="text-[10px] font-medium leading-4 text-slate-500">{currentSizeLabel}</p>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[11px] leading-4">
              <span className="font-semibold text-blue-700" title={currentSize || "Not available"}>
                {currentSizeValueLabel ? `${currentSizeValueLabel}: ` : ""}{currentSize || "Not available"}
              </span>
              {sizeDetails.map((detail) => (
                <span key={detail.label} className="font-semibold text-blue-700" title={detail.value || "Not available"}>
                  {detail.label}: {detail.value || "Not available"}
                </span>
              ))}
            </div>
          </div>
        )}
        {resourceLink && (
          <div className="min-w-0 sm:border-l sm:border-slate-200 sm:pl-6">
            <p className="text-[10px] font-medium leading-4 text-slate-500">{resourceLink.heading}</p>
            <a
              href={resourceLink.url}
              target="_blank"
              rel="noreferrer"
              title={resourceLink.url}
              className="mt-0.5 block truncate text-[11px] font-medium leading-4 text-blue-600 hover:text-blue-700"
            >
              {resourceLink.label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResourceContextPanel;
